import { baseFileName, clamp, escapeXml, haversine, readFileAsText, validateGpxFile } from "./utils.js";

export async function parseGpxFile(file) {
  validateGpxFile(file);
  return parseGpxText(await readFileAsText(file), file.name);
}

export function parseGpxText(gpxText, fileName) {
  const xml = new DOMParser().parseFromString(gpxText, "application/xml");
  if (xml.querySelector("parsererror")) {
    throw new Error("Invalid GPX XML.");
  }

  const groups = getPointGroups(xml);
  if (!groups.length) {
    throw new Error("The GPX file needs at least two track or route points with coordinates.");
  }

  return buildProfile(groups, fileName);
}

export function getSplitBoundaryPoints(profile, fileCount) {
  const splitDistance = profile.totalDistance / fileCount;
  const points = [];

  for (let index = 1; index < fileCount; index += 1) {
    const distance = splitDistance * index;
    points.push(Object.assign(interpolatePoint(profile, distance), { label: `${index}/${fileCount}` }));
  }

  return points;
}

export function buildSplitFiles(profile, fileCount) {
  const splitDistance = profile.totalDistance / fileCount;
  const baseName = baseFileName(profile.fileName, "track");

  return Array.from({ length: fileCount }, (_, index) => {
    const startDistance = splitDistance * index;
    const endDistance = index === fileCount - 1 ? profile.totalDistance : splitDistance * (index + 1);
    const segmentGroups = sliceGroupsByDistance(profile, startDistance, endDistance);
    const fileName = `${baseName}-part-${index + 1}-of-${fileCount}.gpx`;

    return {
      distance: endDistance - startDistance,
      fileName,
      text: buildGpxDocument(`${baseName} part ${index + 1} of ${fileCount}`, segmentGroups),
      type: "application/gpx+xml;charset=utf-8",
    };
  });
}

export function buildMergedGpx(profiles) {
  const segmentGroups = [];

  profiles.forEach((profile) => {
    profile.groups.forEach((group) => {
      segmentGroups.push(group.map(clonePoint));
    });
  });

  return {
    fileName: "merged.gpx",
    text: buildGpxDocument("Merged GPX", segmentGroups),
    type: "application/gpx+xml;charset=utf-8",
  };
}

export function buildKml(profile) {
  const baseName = baseFileName(profile.fileName, "track");

  return {
    fileName: `${baseName}.kml`,
    text: buildKmlDocument(baseName, profile.groups),
    type: "application/vnd.google-earth.kml+xml;charset=utf-8",
  };
}

function buildProfile(sourceGroups, fileName) {
  const groups = [];
  const points = [];
  const segments = [];
  let totalDistance = 0;

  sourceGroups.forEach((sourceGroup) => {
    const group = [withDistance(sourceGroup[0], totalDistance)];
    let previousSourcePoint = sourceGroup[0];
    let previousProfilePoint = group[0];

    for (let index = 1; index < sourceGroup.length; index += 1) {
      const currentSourcePoint = sourceGroup[index];
      const distance = haversine(previousSourcePoint, currentSourcePoint);
      previousSourcePoint = currentSourcePoint;

      if (!Number.isFinite(distance) || distance <= 0) {
        continue;
      }

      const from = previousProfilePoint;
      totalDistance += distance;
      const to = withDistance(currentSourcePoint, totalDistance);
      segments.push({
        from,
        to,
        startDistance: from.distance,
        endDistance: to.distance,
        distance,
      });
      group.push(to);
      previousProfilePoint = to;
    }

    if (group.length >= 2) {
      groups.push(group);
      points.push(...group);
    }
  });

  if (!segments.length || totalDistance <= 0) {
    throw new Error("The GPX file needs at least two track or route points with distance between them.");
  }

  return {
    fileName,
    groups,
    points,
    segments,
    totalDistance,
  };
}

function getPointGroups(xml) {
  const trackSegmentGroups = getNodes(xml, "trkseg")
    .map((segment) => parsePointNodes(segment, "trkpt"))
    .filter((points) => points.length >= 2);

  if (trackSegmentGroups.length) {
    return trackSegmentGroups;
  }

  const trackGroups = getNodes(xml, "trk")
    .map((track) => parsePointNodes(track, "trkpt"))
    .filter((points) => points.length >= 2);

  if (trackGroups.length) {
    return trackGroups;
  }

  const routeGroups = getNodes(xml, "rte")
    .map((route) => parsePointNodes(route, "rtept"))
    .filter((points) => points.length >= 2);

  if (routeGroups.length) {
    return routeGroups;
  }

  const trackPoints = parsePointNodes(xml, "trkpt");
  if (trackPoints.length >= 2) {
    return [trackPoints];
  }

  const routePoints = parsePointNodes(xml, "rtept");
  if (routePoints.length >= 2) {
    return [routePoints];
  }

  return [];
}

function parsePointNodes(root, tagName) {
  return getNodes(root, tagName).map((node) => {
    const elevationNode = getNodes(node, "ele")[0];
    const timeNode = getNodes(node, "time")[0];
    const latitude = Number(node.getAttribute("lat"));
    const longitude = Number(node.getAttribute("lon"));
    const elevationValue = elevationNode ? Number(elevationNode.textContent) : NaN;
    const timeValue = timeNode ? new Date(timeNode.textContent) : null;

    return {
      latitude,
      longitude,
      elevation: Number.isFinite(elevationValue) ? elevationValue : null,
      time: timeValue instanceof Date && !Number.isNaN(timeValue.getTime()) ? timeValue : null,
    };
  }).filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
}

function getNodes(root, tagName) {
  const namespaced = root.getElementsByTagNameNS ? Array.from(root.getElementsByTagNameNS("*", tagName)) : [];
  return namespaced.length ? namespaced : Array.from(root.getElementsByTagName(tagName));
}

function sliceGroupsByDistance(profile, startDistance, endDistance) {
  return profile.groups.map((group) => {
    const groupStart = group[0].distance;
    const groupEnd = group[group.length - 1].distance;
    const overlapStart = Math.max(startDistance, groupStart);
    const overlapEnd = Math.min(endDistance, groupEnd);

    if (overlapEnd <= overlapStart) {
      return [];
    }

    const points = [interpolatePoint(profile, overlapStart)];
    group.forEach((point) => {
      if (point.distance > overlapStart && point.distance < overlapEnd) {
        points.push(clonePoint(point));
      }
    });
    points.push(interpolatePoint(profile, overlapEnd));

    return dedupeAdjacentPoints(points);
  }).filter((group) => group.length >= 2);
}

function interpolatePoint(profile, distance) {
  const targetDistance = clamp(distance, 0, profile.totalDistance);

  if (targetDistance <= 0) {
    return Object.assign(clonePoint(profile.points[0]), { distance: targetDistance });
  }

  if (targetDistance >= profile.totalDistance) {
    return Object.assign(clonePoint(profile.points[profile.points.length - 1]), { distance: targetDistance });
  }

  const segment = profile.segments.find((item) => item.endDistance >= targetDistance);
  if (!segment) {
    return Object.assign(clonePoint(profile.points[profile.points.length - 1]), { distance: targetDistance });
  }

  const ratio = segment.distance > 0 ? (targetDistance - segment.startDistance) / segment.distance : 0;
  return {
    latitude: interpolateValue(segment.from.latitude, segment.to.latitude, ratio),
    longitude: interpolateValue(segment.from.longitude, segment.to.longitude, ratio),
    elevation: interpolateNullableValue(segment.from.elevation, segment.to.elevation, ratio),
    time: interpolateTime(segment.from.time, segment.to.time, ratio),
    distance: targetDistance,
  };
}

function buildGpxDocument(name, segmentGroups) {
  const segments = segmentGroups
    .filter((points) => points.length >= 2)
    .map((points) => `    <trkseg>\n${points.map(formatGpxPoint).join("\n")}\n    </trkseg>`)
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="GPX Transformer" xmlns="http://www.topografix.com/GPX/1/1" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <trk>
    <name>${escapeXml(name)}</name>
${segments}
  </trk>
</gpx>
`;
}

function buildKmlDocument(name, groups) {
  const placemarks = groups
    .filter((points) => points.length >= 2)
    .map((points, index) => `    <Placemark>
      <name>${escapeXml(groups.length > 1 ? `${name} ${index + 1}` : name)}</name>
      <Style><LineStyle><color>ff601bd8</color><width>4</width></LineStyle></Style>
      <LineString>
        <tessellate>1</tessellate>
        <coordinates>
${points.map(formatKmlCoordinate).join("\n")}
        </coordinates>
      </LineString>
    </Placemark>`)
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(name)}</name>
${placemarks}
  </Document>
</kml>
`;
}

function formatGpxPoint(point) {
  const elevation = Number.isFinite(point.elevation) ? `\n        <ele>${roundCoordinate(point.elevation)}</ele>` : "";
  const time = point.time instanceof Date ? `\n        <time>${point.time.toISOString()}</time>` : "";

  return `      <trkpt lat="${roundCoordinate(point.latitude)}" lon="${roundCoordinate(point.longitude)}">${elevation}${time}\n      </trkpt>`;
}

function formatKmlCoordinate(point) {
  const elevation = Number.isFinite(point.elevation) ? point.elevation : 0;
  return `          ${roundCoordinate(point.longitude)},${roundCoordinate(point.latitude)},${roundCoordinate(elevation)}`;
}

function withDistance(point, distance) {
  return Object.assign(clonePoint(point), { distance });
}

function clonePoint(point) {
  return {
    latitude: point.latitude,
    longitude: point.longitude,
    elevation: point.elevation,
    time: point.time,
    distance: point.distance || 0,
  };
}

function dedupeAdjacentPoints(points) {
  return points.filter((point, index) => {
    if (index === 0) {
      return true;
    }

    const previous = points[index - 1];
    return point.latitude !== previous.latitude || point.longitude !== previous.longitude || point.distance !== previous.distance;
  });
}

function interpolateValue(from, to, ratio) {
  return from + (to - from) * ratio;
}

function interpolateNullableValue(from, to, ratio) {
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return null;
  }

  return interpolateValue(from, to, ratio);
}

function interpolateTime(from, to, ratio) {
  if (!(from instanceof Date) || !(to instanceof Date)) {
    return null;
  }

  return new Date(from.getTime() + (to.getTime() - from.getTime()) * ratio);
}

function roundCoordinate(value) {
  return Number(value).toFixed(7).replace(/0+$/, "").replace(/\.$/, "");
}
